import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OverlayModule } from '@angular/cdk/overlay';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { QuillModule } from 'ngx-quill';

import { QUILL_MODULES_TEXTAREA } from '../ts-table/quill-editor.utils';
import { TsButtonModule } from '../ts-button';
import { TsCalloutBoxComponent, TsCalloutBoxType } from '../ts-callout-box';
import { TsDialogComponent } from '../ts-dialog/ts-dialog.component';
import { TSIconModule } from '../ts-icon';

export type TsReviewType = 'approve' | 'request_changes';

export interface TsReviewSubmission {
  type: TsReviewType;
  comment: string;
}

@Component({
  selector: 'ts-review-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    OverlayModule,
    QuillModule,
    TsButtonModule,
    TsCalloutBoxComponent,
    TsDialogComponent,
    TSIconModule,
  ],
  templateUrl: './ts-review-modal.component.html',
  styleUrls: ['./ts-review-modal.component.scss'],
})
export class TsReviewModalComponent {
  readonly placeholder = input<string>('Leave a comment');
  readonly title = input<string>('Finish your review');
  readonly approveCallout = input<string>('');
  readonly requestChangesCallout = input<string>('');

  readonly submitReview = output<TsReviewSubmission>();
  readonly cancelReview = output<void>();

  protected readonly isOpen = signal(false);
  protected readonly comment = signal<string>('');
  protected readonly selectedType = signal<TsReviewType | null>(null);

  protected readonly quillModules = QUILL_MODULES_TEXTAREA;

  protected readonly canSubmit = computed(() => this.selectedType() !== null);

  protected readonly submitColor = computed<'success' | 'warn' | 'basic'>(() => {
    switch (this.selectedType()) {
      case 'approve':
        return 'success';
      case 'request_changes':
        return 'warn';
      default:
        return 'basic';
    }
  });

  protected readonly activeCalloutText = computed<string>(() => {
    switch (this.selectedType()) {
      case 'approve':
        return this.approveCallout();
      case 'request_changes':
        return this.requestChangesCallout();
      default:
        return '';
    }
  });

  protected readonly activeCalloutType = computed<TsCalloutBoxType>(() =>
    this.selectedType() === 'request_changes' ? 'warning' : 'info',
  );

  protected readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
  ];

  protected get commentModel(): string {
    return this.comment();
  }
  protected set commentModel(value: string) {
    this.comment.set(value ?? '');
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.isOpen()) {
      this.close();
    }
  }

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  protected select(type: TsReviewType): void {
    this.selectedType.set(type);
  }

  protected onCancel(): void {
    this.cancelReview.emit();
    this.close();
  }

  protected onSubmit(): void {
    const type = this.selectedType();
    if (!type) return;

    this.submitReview.emit({ type, comment: this.comment() });
    this.close();
  }

  private close(): void {
    this.isOpen.set(false);
    this.selectedType.set(null);
    this.comment.set('');
  }
}
